import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { NonNullableFormBuilder, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, filter, map, take, takeUntil } from 'rxjs/operators';
import { BaseCustomerEmployeesFiltersEnum } from '../../_enums/base-customer-external-employee-filters.enum';
import {
  baseAssignmentsActions,
  baseAssignmentsSelectors,
  BaseCoreState,
  BaseFilterService,
  BaseQueryParamService,
  CustomerAssignmentListItemResponse,
  CustomerDepartmentFilterInfoResponse,
  PspBranchFilterInfoResponse,
  QualificationShortInfoResponse,
  BaseOrderKeyEnum,
  BaseOrderDirectionEnum,
  BaseListPagination,
  BaseAssignmentsListPayloadInterface,
  BaseRolesService,
} from 'base';
import { BaseCustomerExternalEmployeeFilterFormInterface } from '../../_interfaces/base-customer-external-employee-filter-form.interface';
import { ExternalEmployeeService } from '../../external-employee.service';

@Component({
  selector: 'base-customer-external-employees-list',
  templateUrl: './base-customer-external-employee-list.component.html',
})
export class BaseCustomerExternalEmployeeListComponent implements OnInit, OnDestroy {
  @ViewChild('exportContractsPopup', { static: true }) exportContractsPopup: ElementRef;
  customerAssignments: (CustomerAssignmentListItemResponse & { isSelected: boolean })[];
  customerAssignmentsLoaded = false;
  allAssignmentsSelected = false;
  page = BaseListPagination.page;
  size = BaseListPagination.size;
  itemsCount = 0;
  listOrderKey: BaseOrderKeyEnum;
  listOrderDirection: BaseOrderDirectionEnum;
  branches: PspBranchFilterInfoResponse[];
  mainQualifications: QualificationShortInfoResponse[];
  departments: CustomerDepartmentFilterInfoResponse[];
  filters: { [key in BaseCustomerEmployeesFiltersEnum]: any } = {
    [BaseCustomerEmployeesFiltersEnum.status]: undefined,
    [BaseCustomerEmployeesFiltersEnum.mainQualification]: [],
    [BaseCustomerEmployeesFiltersEnum.openContract]: [],
    [BaseCustomerEmployeesFiltersEnum.fromDate]: undefined,
    [BaseCustomerEmployeesFiltersEnum.pspBranch]: [],
    [BaseCustomerEmployeesFiltersEnum.search]: undefined,
    [BaseCustomerEmployeesFiltersEnum.department]: [],
  };
  filtersForm: FormGroup<BaseCustomerExternalEmployeeFilterFormInterface> = this.fb.group({
    status: [[] as number[]],
    mainQualification: [[] as number[]],
    openContract: [[] as number[]],
    fromDate: [''],
    pspBranch: [[] as number[]],
    department: [[] as number[]],
    search: [''],
  });
  isWorkCouncil = false;

  private destroy$ = new Subject();

  constructor(
    private store: Store<BaseCoreState>,
    private filterService: BaseFilterService,
    private queryParamsService: BaseQueryParamService,
    private fb: NonNullableFormBuilder,
    private activatedRoute: ActivatedRoute,
    private rolesService: BaseRolesService,
    private externalEmployeeService: ExternalEmployeeService
  ) {
    [this.listOrderKey, this.listOrderDirection] = this.filterService.setOrderParametersByQueryParameters();
  }

  ngOnInit() {
    this.isWorkCouncil = this.rolesService.hasWorkCouncilRole();
    this.fetchFilteredInquiries();

    this.store
      .select(baseAssignmentsSelectors.getSignContractStatus)
      .pipe(filter((status) => !!status))
      .subscribe(() => {
        this.externalEmployeeService.updateInfoAfterSigning();
      });

    this.store
      .select(baseAssignmentsSelectors.getAllCustomerAssignments)
      .pipe(
        map((state) => state.data),
        takeUntil(this.destroy$)
      )
      .subscribe((list) => {
        this.customerAssignments = list.data.map(
          (item: CustomerAssignmentListItemResponse & { isSelected: boolean }) => {
            const isAssignmentSelected = this.customerAssignments?.some(
              (assignment) => assignment.id === item.id && assignment.isSelected
            );
            return { ...item, ...{ isSelected: isAssignmentSelected } };
          }
        );
        this.allAssignmentsSelected =
          this.customerAssignments.length > 0 && this.customerAssignments.every((assignment) => assignment.isSelected);
        this.page = list.meta.page.current;
        this.size = list.meta.page.size;
        this.itemsCount = list.meta.page.itemsCount;
        this.customerAssignmentsLoaded = true;
      });

    this.store
      .select(baseAssignmentsSelectors.getCustomerAssignmentsFilters)
      .pipe(take(1))
      .subscribe((filters) => {
        this.branches = filters.branches;
        this.mainQualifications = filters.mainQualifications;
        this.departments = filters.departments;
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  selectAssignment(event: number) {
    this.customerAssignments.map((assignment) => {
      if (assignment.id === event) {
        assignment.isSelected = !assignment.isSelected;
      }
    });
    this.allAssignmentsSelected =
      this.customerAssignments.length > 0 && this.customerAssignments.every((assignment) => assignment.isSelected);
  }

  selectAll(event: boolean) {
    this.allAssignmentsSelected = event;
    this.customerAssignments.map((assignment) => (assignment.isSelected = event));
  }

  sortAssignments(event: { direction: BaseOrderDirectionEnum; key: BaseOrderKeyEnum }): void {
    this.listOrderDirection = event.direction;
    this.listOrderKey = event.key;
    this.queryParamsService.addParameter('order', this.listOrderKey + ',' + this.listOrderDirection);
    this.queryParamsService.filterSubject.next({ page: 1 });
  }

  exportContracts(): void {
    const queryParams = this.activatedRoute.snapshot.queryParams;

    const payload = {
      search: queryParams.search,
      orderKey: queryParams?.order?.split(',')[0],
      orderDirection: queryParams?.order?.split(',')[1],
      fromDate: queryParams.fromDate,
      'statuses[]': queryParams.status,
      'mainQualifications[]': queryParams.mainQualification,
      'openContracts[]': queryParams.openContract,
      'pspBranches[]': queryParams.pspBranch,
      'assignments[]': this.getSelectedAssignments(),
    };
    this.store.dispatch(baseAssignmentsActions.downloadCustomerContracts({ payload }));
  }

  exportAssignmentInfo(): void {
    const queryParams = this.activatedRoute.snapshot.queryParams;
    const selectAssignments = this.getSelectedAssignments();

    const payload = {
      search: queryParams.search,
      'mainQualifications[]': queryParams.mainQualification,
      'departments[]': queryParams.department,
      'pspBranches[]': queryParams.pspBranch,
      fromDate: queryParams.fromDate,
      'statuses[]': queryParams.status,
      'openContracts[]': queryParams.openContract,
      'assignments[]': !!selectAssignments.length ? selectAssignments.join(',') : undefined,
      orderKey: queryParams?.order?.split(',')[0],
      orderDirection: queryParams?.order?.split(',')[1],
    };
    this.store.dispatch(baseAssignmentsActions.downloadCustomerAssignmentInfo({ payload }));
  }

  private getSelectedAssignments(): number[] {
    return this.customerAssignments.filter((assignment) => assignment.isSelected).map((assignment) => assignment.id);
  }

  private fetchFilteredInquiries() {
    this.queryParamsService.filterSubject.pipe(takeUntil(this.destroy$), debounceTime(50)).subscribe((value) => {
      this.queryParamsService.redirectWithQueryParams(value, { search: 'value' }, () => this.loadAssignments());
    });
  }

  private loadAssignments(): void {
    this.store.dispatch(
      baseAssignmentsActions.loadCustomerAllAssignments({
        payload: this.activatedRoute.snapshot.queryParams as BaseAssignmentsListPayloadInterface,
      })
    );
  }
}
